import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";

import { ThemeContext } from "./ThemeContext";
import { applyResolvedTheme, getNextTheme, resolveTheme } from "./applyTheme";
import {
    DEFAULT_THEME,
    THEME_DARK,
    THEME_LIGHT,
    THEME_PICKER_ENABLED,
} from "./themeConstants";
import { isValidTheme, readStoredTheme, writeStoredTheme } from "./themeStorage";

/**
 * Owns the theme preference and keeps <html data-theme> in step with it.
 * Mounted once, above the router, in App.js.
 */
const ThemeProvider = ({ children }) => {
    // Seeded from storage during the first render rather than in an effect, so
    // the first paint is already correct. The inline script in index.html has
    // normally set the attribute before this runs; reading the same key here
    // keeps React's idea of the theme and the DOM's from disagreeing.
    // With the picker off, storage is left alone but not read: a preference
    // saved while dark was available shouldn't outlive it on screen.
    const [theme, setThemeState] = useState(() =>
        THEME_PICKER_ENABLED ? readStoredTheme() ?? DEFAULT_THEME : THEME_LIGHT,
    );

    // There is nothing to resolve against any more — the preference is already
    // one of the two paintable values — but anything that got past the storage
    // guard is narrowed here rather than reaching the DOM.
    const resolvedTheme = resolveTheme(theme);

    /* Pages that take no theme (the login screen) register here rather than
       writing the attribute themselves. Their effects run before this one — a
       child's always does — so anything they painted would be overwritten a
       moment later by the preference. Held as a count, not a flag, so an
       overlapping mount and unmount can't leave the app pinned light. */
    const [unthemedPages, setUnthemedPages] = useState(0);

    const registerUnthemedPage = useCallback(() => {
        setUnthemedPages((count) => count + 1);
        return () => setUnthemedPages((count) => count - 1);
    }, []);

    const paintedTheme = unthemedPages > 0 ? THEME_LIGHT : resolvedTheme;

    // Layout-effect timing isn't needed: the boot script already painted the
    // right theme, so this only corrects the attribute when the preference
    // changes, never on the critical first-paint path.
    useEffect(() => {
        applyResolvedTheme(paintedTheme);
    }, [paintedTheme]);

    const setTheme = useCallback((next) => {
        // Inert while the picker is hidden — there is no control that reaches
        // here, and anything that finds its way in shouldn't paint dark.
        if (!THEME_PICKER_ENABLED) {return;}
        if (!isValidTheme(next)) {return;}
        setThemeState(next);
        // A failed write (private mode, blocked storage) still leaves the theme
        // applied for this session — it just won't survive a reload.
        writeStoredTheme(next);
    }, []);

    const toggleTheme = useCallback(() => {
        setTheme(getNextTheme(theme));
    }, [theme, setTheme]);

    const value = useMemo(
        () => ({
            theme,
            resolvedTheme,
            isDark: resolvedTheme === THEME_DARK,
            setTheme,
            toggleTheme,
            registerUnthemedPage,
            isFallback: false,
        }),
        [theme, resolvedTheme, setTheme, toggleTheme, registerUnthemedPage],
    );

    return (
        <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
    );
};

ThemeProvider.propTypes = {
    children: PropTypes.node,
};

export default ThemeProvider;
